import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { PRODUCTS as FALLBACK } from "../mock";
import { fetchProducts } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const ProductCard = ({ p }) => {
  const { addItem } = useCart();
  const compareAt = p.compare_at ?? p.compareAt;
  return (
    <div className="hover-lift bg-[#FFF2EC] rounded-2xl overflow-hidden border border-[#1A1410]/10 flex flex-col group">
      <Link
        to={`/products/${p.slug}`}
        className="relative block aspect-[4/5] overflow-hidden bg-[#FFE4D6]"
      >
        <img
          src={p.images[0]}
          alt={p.name}
          className="product-img absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {p.badge && (
          <div className="absolute top-3 left-3 bg-[#1A1410] text-[#FFF2EC] text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
            {p.badge}
          </div>
        )}
        {p.tag && (
          <div className="absolute top-3 right-3 bg-[#DD4D2A] text-white text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
            {p.tag}
          </div>
        )}
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.floor(p.rating)
                    ? "fill-[#DD4D2A] text-[#DD4D2A]"
                    : "text-[#1A1410]/20"
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-[#1A1410]/60">
            ({p.rating}) · {p.reviews.toLocaleString()} reviews
          </span>
        </div>
        <h3 className="font-serif-display text-lg md:text-xl font-semibold leading-tight text-[#1A1410] line-clamp-2">
          {p.name}
        </h3>
        <p className="mt-2 text-sm text-[#1A1410]/65 line-clamp-2">
          {p.shortDesc}
        </p>
        <div className="mt-4 flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-lg text-[#1A1410]">
              ${p.price}.00
            </span>
            <span className="text-sm text-[#1A1410]/45 line-through">
              ${compareAt}.00
            </span>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => {
              addItem({
                id: p.id,
                name: p.name,
                price: p.price,
                compareAt: compareAt,
                images: p.images,
              });
              toast.success(`${p.name} added to cart`);
            }}
            className="btn-coral flex-1 py-2.5 rounded-full text-[12px] uppercase"
          >
            Add to cart
          </button>
          <Link
            to={`/products/${p.slug}`}
            className="btn-outline-dark px-4 py-2.5 rounded-full text-[12px] uppercase inline-flex items-center gap-1"
          >
            View
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const ProductGrid = () => {
  const [products, setProducts] = useState(FALLBACK);

  useEffect(() => {
    fetchProducts()
      .then((data) => {
        if (Array.isArray(data) && data.length) setProducts(data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="shop" className="bg-[#FFE4D6]">
      <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10 md:mb-14">
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold mb-3">
              Shop the Collection
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1A1410]">
              Most <span className="italic">Popular</span>
            </h2>
          </div>
          <Link
            to="/collections"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-[#1A1410] hover:text-[#DD4D2A] transition-colors"
          >
            View all products
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} p={p} />
          ))}
        </div>
        <div className="mt-10 md:hidden flex justify-center">
          <Link
            to="/collections"
            className="btn-outline-dark inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase"
          >
            View all products
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductGrid;
